/* eslint-disable import/no-extraneous-dependencies */
import React, {
   createContext,
   useContext,
   useMemo,
   useState,
   useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
   LuPackage,
   LuWallet,
   LuUsers,
   LuMap,
   LuBuilding2,
} from 'react-icons/lu';

// ---------------------------------------------------------------------------
// Configuração dos módulos do sistema
//
// "configuracoes" não entra aqui — ele é renderizado fixo no MenuItems,
// independente do módulo ativo, para quem tem a permissão adequada.
//
// Dois campos de ícone, de propósito (2026-08-22):
//   `icon`          — emoji, consumido pelo ModuleSelector da sidebar (legado).
//   `iconComponent` — ícone Lucide (`react-icons/lu`), consumido pelos cards da
//                     home. O handoff de design pede Lucide stroke 1.5 a 30px
//                     no card de módulo; emoji é colorido e não aceita `color`.
// ---------------------------------------------------------------------------
export const MODULE_CONFIG = {
   estoque: {
      label: 'Estoque',
      icon: '📦',
      iconComponent: LuPackage,
      requiredPermissions: [
         'inventory:stock:read',
         'inventory:movement:read',
         'inventory:movement:create',
         'inventory:product:read',
         'inventory:advanced_stock:search',
         // Transferência entre PDVs (handoff 0099): quem só tiver a leitura de
         // transferências também precisa enxergar o módulo Estoque.
         'inventory:transfer:read',
      ],
   },
   financeiro: {
      label: 'Financeiro',
      icon: '💰',
      iconComponent: LuWallet,
      requiredPermissions: [
         'finance:charge:read',
         'finance:charge:create',
         // Contas bancárias & credenciais de integração (handoff 0079). Quem
         // tiver só a gestão das contas/chaves também enxerga o módulo.
         'finance:bank_account:read',
         'finance:bank_account:create',
         'finance:bank_account:update',
         'finance:credential:read',
         'finance:credential:manage',
         // Régua de cobrança (handoff 0090): operador de cobrança pode ter SÓ
         // as chaves de dunning — sem esta entrada ele não enxergava o módulo
         // (permission-interlocking: item de menu gateado certo, módulo não).
         'finance:dunning:read',
         // Acerto com o escritório (0103): chave PRÓPRIA, não implicada por
         // `dunning:read`. Sem esta entrada, um perfil que só confere o acerto
         // teria o item de menu gateado certo e o módulo fechado — a mesma
         // permission-interlocking de cima.
         'finance:dunning:reconcile',
         // Painel de crediário (0084) — mesmo caso: usuário só-dashboard.
         'finance:dashboard:read',
      ],
   },
   funcionários: {
      label: 'Funcionários',
      icon: '👤',
      iconComponent: LuUsers,
      requiredPermissions: [
         'hr:employee:read',
         'hr:employee:create',
         'hr:salary_advance:read',
         'hr:salary_advance:create',
         'hr:salary_payment:read',
         'hr:advance_request:read',
         // Submódulo Recrutamento & Seleção (handoff 0061). Quem tiver só perm
         // de recrutamento também enxerga o módulo no seletor.
         'hr:candidate:read',
         'hr:candidate:create',
         'hr:candidate_interview:read',
         'hr:job_opening:read',
         'hr:recruitment_catalog:manage',
         // Fase 1 — onboarding/admissão (handoff 0065).
         'hr:candidate_onboarding:read',
         'hr:job_position_requirement:read',
      ],
   },
   roteirização: {
      label: 'Roteirização',
      icon: '🗺️',
      iconComponent: LuMap,
      requiredPermissions: [
         'routing:schedule_item:read',
         'routing:schedule_item:update',
         'routing:schedule_item:update_status',
         'routing:import:create',
         'routing:report:read',
      ],
   },
   comercial: {
      label: 'Comercial',
      icon: '🏢',
      iconComponent: LuBuilding2,
      requiredPermissions: [
         'customers:customer:read',
         'customers:customer:create',
         'customers:customer:update',
         'customers:customer:delete',
      ],
   },
};

const STORAGE_KEY = 'app:activeModule';
const ModuleContext = createContext(null);

export function ModuleProvider({ children }) {
   const [activeModule, setActiveModule] = useState(() => {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved && MODULE_CONFIG[saved] ? saved : null;
   });

   const selectModule = useCallback((moduleKey) => {
      if (moduleKey && MODULE_CONFIG[moduleKey]) {
         localStorage.setItem(STORAGE_KEY, moduleKey);
         setActiveModule(moduleKey);
      } else {
         localStorage.removeItem(STORAGE_KEY);
         setActiveModule(null);
      }
   }, []);

   // useMemo garante que o objeto de value só recria quando activeModule muda,
   // evitando re-renders desnecessários em todos os consumidores do contexto.
   const contextValue = useMemo(
      () => ({ activeModule, selectModule }),
      [activeModule, selectModule]
   );

   return (
      <ModuleContext.Provider value={contextValue}>
         {children}
      </ModuleContext.Provider>
   );
}

ModuleProvider.propTypes = {
   children: PropTypes.node.isRequired,
};

export function useModule() {
   const ctx = useContext(ModuleContext);
   if (!ctx) {
      throw new Error('useModule deve ser usado dentro de <ModuleProvider>');
   }
   return ctx;
}
